import _ from 'lodash';
import { createSelector } from 'reselect';
import shortid from 'shortid';

import {
  convertScannedStudentToAppStudent,
  getIncomeFrequency,
  convertScannedMemberToAppMember,
} from 'Pages/reviewincomingapplications/helpers';
import {
  ApplicationFieldEnum as ApplicationFields,
  ScannedApplicationFieldEnum as ScannedAppFields,
} from 'Common/FieldEnum';
import * as ManageAppHelpers from 'Pages/manageapplication/helpers/Helpers';
import {
  CustomMemberFields,
  IncomeComparisonFields,
} from 'Pages/reviewincomingapplications/helpers/Constants';
import { createMap } from 'Common/Helpers/format';

import { EXTRA_HOH_FIELDS } from '../../helpers';

const root = (state) =>
  _.get(state, 'ReviewIncomingApplicationsState.duplicateApps');

export const getAppComparison = (state) =>
  _.get(root(state), 'appComparisonState.data');

export const getAppComparisonLoading = (state) =>
  _.get(root(state), 'appComparisonState.loading', false);

export const getIncomingApplication = (state) =>
  _.get(getAppComparison(state), 'ApplicationMatch.Source');

export const getExistingApplication = (state) =>
  _.get(getAppComparison(state), 'ApplicationMatch.Target');

export const getExistingHouseholdContactInfo = (state) =>
  _.get(getExistingApplication(state), 'HouseholdContactInfo');

export const getExistingAppMembers = (state) =>
  _.get(getExistingApplication(state), 'Members');

export const getExistingAppStudents = (state) =>
  _.get(getExistingApplication(state), 'Students');

export const getDuplicateStudentMatches = (state) =>
  _.get(getAppComparison(state), 'DuplicateStudentsMatch', []);

export const getExistingStudentsNotOnApplication = (state) =>
  _.get(getAppComparison(state), 'ExistingStudents', []);

/**
 * Returns student ids mapped to student object.
 */
export const getExistingStudentsNotOnApplicationMap = createSelector(
  getExistingStudentsNotOnApplication,
  (existingStudents) => {
    return createMap(existingStudents);
  }
);

export const getExistingAppHouseholdMembers = createSelector(
  getExistingAppMembers,
  getExistingAppStudents,
  getExistingHouseholdContactInfo,
  (existingMembers, existingStudents, householdInfo) => {
    let householdMembers = [];
    let HOH = [];
    let members = [];

    if (Array.isArray(existingMembers)) {
      existingMembers.forEach((m) => {
        if (
          ManageAppHelpers.getMemberType(m[ApplicationFields.Id]) ===
          ManageAppHelpers.MemberTypeEnum.HOH
        ) {
          HOH.push({ ...m, ..._.pick(householdInfo, EXTRA_HOH_FIELDS) });
        } else {
          members.push(m);
        }
      });
    }

    householdMembers = householdMembers.concat(HOH);
    if (Array.isArray(existingStudents)) {
      householdMembers = householdMembers.concat(existingStudents);
    }
    householdMembers = householdMembers.concat(members);

    return householdMembers;
  }
);

export const getDuplicateApplicationsState = (state) =>
  _.get(root(state), 'duplicateApplicationsState');

export const getDuplicateApplications = (state) =>
  _.get(getDuplicateApplicationsState(state), 'data', []);

export const getLoadingDuplicateApps = (state) =>
  _.get(getDuplicateApplicationsState(state), 'loading', false);

export const getDuplicateApplicationsError = (state) =>
  _.get(getDuplicateApplicationsState(state), 'error', null);

export const getDuplicateAppsForTable = createSelector(
  getDuplicateApplications,
  (duplicateApps) => {
    return duplicateApps.map((app) => {
      const householdInfo = app[ApplicationFields.HouseholdContactInfo] || {};
      const applicationInfo =
        app[ApplicationFields.ApplicationInformation] || {};

      const HOH =
        (app[ApplicationFields.Members] || []).find(
          (m) => m[ApplicationFields.IsHOH]
        ) || {};

      const students = (app[ApplicationFields.Students] || []).map((s) => ({
        [ScannedAppFields.MemberFirstName]: s[ApplicationFields.FirstName],
        [ScannedAppFields.MemberLastName]: s[ApplicationFields.LastName],
        [ScannedAppFields.MemberStudentNumber]:
          s[ApplicationFields.ServingNumber],
        [ScannedAppFields.MemberSchoolNumber]: s[ApplicationFields.SiteId],
        [ScannedAppFields.BirthDateDT]: s[ApplicationFields.BirthDate],
      }));

      return {
        [ScannedAppFields.HouseholdFirstName]:
          HOH[ApplicationFields.FirstName] || '',
        [ScannedAppFields.HouseholdLastName]:
          HOH[ApplicationFields.LastName] || '',
        [ScannedAppFields.HouseholdAddress1]:
          householdInfo[ApplicationFields.Address1] || '',
        [ScannedAppFields.HouseholdAddress2]:
          householdInfo[ApplicationFields.Address2] || '',
        [ScannedAppFields.Zip]: householdInfo[ApplicationFields.ZipCode] || '',
        [ScannedAppFields.City]: householdInfo[ApplicationFields.City] || '',
        [ScannedAppFields.State]: householdInfo[ApplicationFields.State] || '',
        [ScannedAppFields.ApplicationDate]:
          applicationInfo[ApplicationFields.ApplicationDate],
        [ScannedAppFields.Members]: students,
        [ApplicationFields.Id]: applicationInfo[ApplicationFields.FamilyNumber],
      };
    });
  }
);

export const getIncomingAppMembers = createSelector(
  getIncomingApplication,
  (incomingApplication) => {
    const incomingMembers = _.get(incomingApplication, 'Members', []);

    return incomingMembers.map((m) => {
      // Members from LAP will have their student number set to 0 and students that don't have
      // an id provided will also be set to 0. Check against the MemberSchoolNumber to determine
      // if the person is a member
      if (
        m[ScannedAppFields.MemberStudentNumber] === '0' &&
        !m[ScannedAppFields.MemberSchoolNumber]
      ) {
        return { ...m, [ScannedAppFields.MemberStudentNumber]: null };
      }

      return m;
    });
  }
);

export const getIncomingAppHouseHoldMembers = createSelector(
  getIncomingApplication,
  getIncomingAppMembers,
  getExistingAppHouseholdMembers,
  getExistingStudentsNotOnApplicationMap,
  (
    incomingApp,
    incomingMembers,
    existingAppMembers,
    existingStudentsNotOnAppMap
  ) => {
    let householdMembers = [];

    if (incomingApp) {
      const headOfHousehold = {
        [ApplicationFields.EmancipatedStudentId]: null,
        [ApplicationFields.FirstName]:
          incomingApp[ScannedAppFields.HouseholdFirstName],
        [ApplicationFields.LastName]:
          incomingApp[ScannedAppFields.HouseholdLastName],
        [ApplicationFields.Id]: null,
        [ApplicationFields.IsHOH]: true,
        [ApplicationFields.Income]: {
          [ApplicationFields.Id]: null,
          [ApplicationFields.Income1]:
            incomingApp[ScannedAppFields.HouseholdIncome1],
          [ApplicationFields.Income2]:
            incomingApp[ScannedAppFields.HouseholdIncome2],
          [ApplicationFields.Income3]:
            incomingApp[ScannedAppFields.HouseholdIncome3],
          [ApplicationFields.Income4]:
            incomingApp[ScannedAppFields.HouseholdIncome4],
          [ApplicationFields.Freq1]: getIncomeFrequency(
            incomingApp[ScannedAppFields.HouseholdIncome1],
            incomingApp[ScannedAppFields.Freq1]
          ),
          [ApplicationFields.Freq2]: getIncomeFrequency(
            incomingApp[ScannedAppFields.HouseholdIncome2],
            incomingApp[ScannedAppFields.Freq2]
          ),
          [ApplicationFields.Freq3]: getIncomeFrequency(
            incomingApp[ScannedAppFields.HouseholdIncome3],
            incomingApp[ScannedAppFields.Freq3]
          ),
          [ApplicationFields.Freq4]: getIncomeFrequency(
            incomingApp[ScannedAppFields.HouseholdIncome4],
            incomingApp[ScannedAppFields.Freq4]
          ),
        },
        [ApplicationFields.LanguageId]:
          incomingApp[ScannedAppFields.LanguageId],
        [ApplicationFields.SSN]: null,
        [ApplicationFields.Address1]:
          incomingApp[ScannedAppFields.HouseholdAddress1],
        [ApplicationFields.Address2]:
          incomingApp[ScannedAppFields.HouseholdAddress2],
        [ApplicationFields.ZipCode]: incomingApp[ScannedAppFields.Zip],
        [ApplicationFields.State]: incomingApp[ScannedAppFields.State],
        [ApplicationFields.City]: incomingApp[ScannedAppFields.City],
        [ApplicationFields.IncomingHOHId]: shortid.generate(),
      };

      householdMembers.push(headOfHousehold);
    }

    if (Array.isArray(incomingMembers)) {
      householdMembers = householdMembers.concat(
        incomingMembers
          .filter((m) => !_.isNil(m.MemberStudentNumber))
          .map((m) => {
            const convertedStudent = convertScannedStudentToAppStudent(
              m,
              incomingApp,
              existingStudentsNotOnAppMap,
              incomingApp[ScannedAppFields.LanguageId]
            );

            // If the student already exists, need to give the student its IdSeed otherwise treated as a new student
            if (convertedStudent[ApplicationFields.Id]) {
              const existingStudent = existingAppMembers.find(
                (eam) =>
                  eam[ApplicationFields.Id] ===
                  convertedStudent[ApplicationFields.Id]
              );
              convertedStudent[ApplicationFields.IdSeed] = existingStudent
                ? existingStudent[ApplicationFields.IdSeed]
                : 0;
            }

            return convertedStudent;
          })
      );

      householdMembers = householdMembers.concat(
        incomingMembers
          .filter((m) => _.isNil(m.MemberStudentNumber))
          .map((m) => convertScannedMemberToAppMember(m))
      );
    }

    householdMembers.forEach((hm) => {
      const matchingMember = existingAppMembers.find((m) => {
        if (
          (m[ApplicationFields.IsHOH] && hm[ApplicationFields.IsHOH]) ||
          (ManageAppHelpers.getMemberType(m[ApplicationFields.Id]) ===
            ManageAppHelpers.MemberTypeEnum.Member &&
            ManageAppHelpers.getMemberType(hm[ApplicationFields.Id]) ===
              ManageAppHelpers.MemberTypeEnum.Member)
        ) {
          return (
            m[ApplicationFields.FirstName].toLowerCase().trim() ===
              hm[ApplicationFields.FirstName].toLowerCase().trim() &&
            m[ApplicationFields.LastName].toLowerCase().trim() ===
              hm[ApplicationFields.LastName].toLowerCase().trim()
          );
        }
        return m[ApplicationFields.Id] === hm[ApplicationFields.Id];
      });

      let diffs = {};

      if (matchingMember != null) {
        Object.keys(matchingMember).forEach((k) => {
          if (k === ApplicationFields.Income) {
            const incomeDiff = {};
            IncomeComparisonFields.forEach((incomeField) => {
              incomeDiff[incomeField] =
                matchingMember[ApplicationFields.Income][incomeField] !==
                hm[ApplicationFields.Income][incomeField];
            });

            diffs[k] = incomeDiff;
          } else {
            diffs[k] = !_.isEqual(matchingMember[k], hm[k]);
          }
        });
      } else {
        // All of the info is new to the app
        Object.keys(hm).forEach((k) => {
          diffs[k] = true;
        });
      }

      hm[CustomMemberFields.DifferingProperties] = diffs;

      return hm;
    });
    return householdMembers;
  }
);

export const getAppComparisonError = (state) =>
  _.get(root(state), 'appComparisonState.error');

export const getErrors = createSelector(
  getAppComparisonError,
  getDuplicateApplicationsError,
  (...args) => {
    const errors = [];

    args.forEach((error) => {
      if (error) {
        errors.push(error);
      }
    });

    return _.flattenDeep(errors);
  }
);

export const getIncomingHOH = createSelector(
  getIncomingApplication,
  (incomingApplication) => {
    return _.pickBy(
      incomingApplication,
      (value, key) => key !== ScannedAppFields.Members
    );
  }
);



// WEBPACK FOOTER //
// ./src/pages/reviewincomingapplications/state/duplicateapps/selectors.js